import React from "react";
import { Box, Flex } from "@chakra-ui/core";
import { StoryCard } from "components/shared/StoryCard";
import { StoryCardCondensed } from "components/shared/StoryCardCondensed";
import { ApiStory, getStoryId } from "data/api/ApiStory";
import useViewport from "hooks/useViewport";
import styled from "styled-components";

const Text = styled.h2.attrs({
  className: "text-xl lg:text-2xl font-semibold text-left w-full mb-5",
})`
  font-family: ${(props) => props.theme.fonts.poppins};
  max-width: 1000px;
`;

export function StoryListContainer({
  children,
  title,
}: {
  children?: React.ReactNode;
  title?: string;
}): JSX.Element {
  return (
    <Flex
      padding={4}
      justifyContent="center"
      direction={"column"}
      width={"100%"}
      maxWidth="1000px"
    >
      {title && <Text>{title}</Text>}
      <Flex flexGrow={1} justifyContent="center">
        {children}
      </Flex>
    </Flex>
  );
}

export function StoryListItem({
  children,
  onClick,
}: {
  children?: React.ReactNode;
  onClick?: () => void;
}): JSX.Element {
  return (
    <Box pb={2} onClick={onClick ? onClick : null}>
      {children}
    </Box>
  );
}

export function LeftColumn({
  children,
}: {
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <Flex pr={2} flexDirection="column" w="50%">
      {children}
    </Flex>
  );
}

export function RightColumn({
  children,
}: {
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <Flex pl={2} flexDirection="column" w="50%">
      {children}
    </Flex>
  );
}

export function TwoColumnStoryList({
  stories,
  onClickStory,
}: {
  stories: ApiStory[];
  onClickStory?: (story: ApiStory) => void;
}): JSX.Element {
  function storyIsInLeftColumn(index) {
    return index % 2 === 0;
  }

  function storyIsInRightColumn(index) {
    return index % 2 !== 0;
  }

  function leftColumnStories() {
    return stories.filter((_, index) => storyIsInLeftColumn(index));
  }

  function rightColumnStories() {
    return stories.filter((_, index) => storyIsInRightColumn(index));
  }

  return (
    <>
      <LeftColumn>
        {leftColumnStories().map((story) => (
          <StoryListItem
            key={getStoryId(story)}
            onClick={onClickStory ? () => onClickStory(story) : null}
          >
            <StoryCard story={story} />
          </StoryListItem>
        ))}
      </LeftColumn>
      <RightColumn>
        {rightColumnStories().map((story) => (
          <StoryListItem
            key={getStoryId(story)}
            onClick={onClickStory ? () => onClickStory(story) : null}
          >
            <StoryCard story={story} />
          </StoryListItem>
        ))}
      </RightColumn>
    </>
  );
}

export function SingleColumnStoryList({
  stories,
  onClickStory,
}: {
  stories: ApiStory[];
  onClickStory?: (story: ApiStory) => void;
}): JSX.Element {
  const { isMobileOrSmaller } = useViewport();

  return (
    <Flex flexDirection="column" w="100%">
      {stories.map((story) => (
        <StoryListItem
          key={getStoryId(story)}
          onClick={onClickStory ? () => onClickStory(story) : null}
        >
          {isMobileOrSmaller ? (
            <StoryCard story={story} />
          ) : (
            <StoryCardCondensed story={story} />
          )}
        </StoryListItem>
      ))}
    </Flex>
  );
}

export function StoryList({
  stories,
  useSingleColumn,
  onClickStory,
  title,
}: {
  stories: any[];
  useSingleColumn?: boolean;
  onClickStory?: (story: ApiStory) => void;
  title?: string;
}): JSX.Element {
  const { isMobileOrSmaller } = useViewport();

  return (
    <StoryListContainer title={title}>
      {useSingleColumn || isMobileOrSmaller ? (
        <SingleColumnStoryList stories={stories} onClickStory={onClickStory} />
      ) : (
        <TwoColumnStoryList stories={stories} onClickStory={onClickStory} />
      )}
    </StoryListContainer>
  );
}
